import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import ApiService from '../../../services/api';
import { useUserProfile } from '@/hooks/useApi';
import { FileUploader } from '@/components';
import Select from "react-select";

const LoansCreate = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    account: '',
    name: '',
    min_plafond: '',
    max_plafond: '',
    method_loan: '',
    min_time_period: '',
    max_time_period: '',
    need_guarantee: false,
    guarantee: '',
    benefits_loan: '',
    requirement: '',
    image_file: '',
  });

  const handleInputChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const methodList = [
    { value: '1', label: 'Fixed' },
    { value: '2', label: 'Effective' },
    { value: '3', label: 'Anuitas' },
  ]

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name || !formData.phone_number || !formData.occupation || !formData.cooperative_id) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please fill in all required fields'
      });
      return;
    }

    setSaving(true);
    try {
      // Prepare data for API
      const loansData = {
        ...formData,
      };

      // Remove empty fields
      Object.keys(loansData).forEach(key => {
        if (loansData[key] === '') {
          delete loansData[key];
        }
      });

      const response = await ApiService.post('/loans', loansData);
      
      if (response.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Member created successfully'
        });
        navigate('/loans');
      } else {
        throw new Error(response.message || 'Failed to create loans');
      }
    } catch (error) {
      console.error('Error creating loans:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to create loans'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col gap-3 items-start sm:flex-col sm:items-start sm:justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link 
              to="/loans"
              className="text-blue-600 hover:text-blue-800 flex items-center"
            >
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Loans
            </Link>
          </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create New Loans</h1>
              <p className="text-gray-600">Add a new loans</p>
            </div>
        </div>
      </div>

      {/* Create Form */}
      <div className="max-w-7xl mx-auto">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Account Code */}
            <div className="md:col-span-2">
              <label htmlFor="account" className="block text-sm font-medium text-gray-700 mb-2">
                Account Code *
              </label>
              <input
                type="text"
                id="account"
                name="account"
                value={formData.account}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter Account Code"
              />
            </div>

            {/* Loan Name */}
            <div className="md:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Loan Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter loan name"
              />
            </div>

            {/* Minimal Plafond */}
            <div>
              <label htmlFor="min_plafond" className="block text-sm font-medium text-gray-700 mb-2">
                Minimal Plafond *
              </label>
              <input
                type="number"
                id="min_plafond"
                name="min_plafond"
                value={formData.min_plafond}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter Minimal Plafond"
              />
            </div>
            {/* Maximal Plafond */}
            <div>
              <label htmlFor="max_plafond" className="block text-sm font-medium text-gray-700 mb-2">
                Maximal Plafond *
              </label>
              <input
                type="number"
                id="max_plafond"
                name="max_plafond"
                value={formData.max_plafond}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter Maximal Plafond"
              />
            </div>

            {/* Minimal Time Period */}
            <div>
              <label htmlFor="min_time_period" className="block text-sm font-medium text-gray-700 mb-2">
                Minimal Time Period (Month) *
              </label>
              <input
                type="number"
                id="min_time_period"
                name="min_time_period"
                value={formData.min_time_period}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter Minimal Time Period"
              />
            </div>
            {/* Maximal Time Period */}
            <div>
              <label htmlFor="max_time_period" className="block text-sm font-medium text-gray-700 mb-2">
                Maximal Time Period (Month) *
              </label>
              <input
                type="number"
                id="max_time_period"
                name="max_time_period"
                value={formData.max_time_period}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter Maximal Time Period"
              />
            </div>
            
            <div className="md:col-span-2">
              <label htmlFor="method_loan" className="block text-sm font-medium text-gray-700 mb-2">
                Method Loan
              </label>
              <Select options={methodList} onChange={handleInputChange} value={formData.method_loan} placeholder="Choose Method Loan" />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="guarantee" className="block text-sm font-medium text-gray-700 mb-2">
                Need Guarantee
              </label>
              <input
                className="form-switch"
                type="checkbox"
                name="need_guarantee"
                role="switch"
                checked={formData.need_guarantee}
                onChange={handleInputChange}
                id="flexSwitchCheckDefault"
              />
            </div>

            {formData.need_guarantee && (
              <div className="md:col-span-2">
                <label htmlFor="guarantee" className="block text-sm font-medium text-gray-700 mb-2">
                  Guarantee
                </label>
                <input
                  type="text"
                  id="guarantee"
                  name="guarantee"
                  value={formData.guarantee}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter Maximal Plafond"
                />
              </div>
            )}
            
            <div className="md:col-span-2">
              <label htmlFor="benefits_loan" className="block text-sm font-medium text-gray-700 mb-2">
                Benefits
              </label>
              <textarea
                type="text"
                id="benefits_loan"
                name="benefits_loan"
                value={formData.benefits_loan}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="requirement" className="block text-sm font-medium text-gray-700 mb-2">
                Requirement
              </label>
              <textarea
                type="text"
                id="requirement"
                name="requirement"
                value={formData.requirement}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
                Photo
              </label>
              <FileUploader
                showPreview={true}
                icon="mgc_upload_2_line"
                text="Click or drag file to upload"
                extraText="Supported: jpg, png"
                onFileUpload={(files) => {
                  setFormData(prev => ({
                    ...prev,
                    image_file: files[0] || null
                  }));
                }}
              />
            </div>
          </div>
          

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <Link
              to="/members"
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Creating...' : 'Create Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoansCreate;
